import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../utils/api';
import LogTemplateBuilder, { LogTemplate } from '../components/logs/LogTemplateBuilder';

const categoryLabels: Record<string, string> = {
  safety: 'Safety',
  injury: 'Injury Incident',
  workplace_violence: 'Workplace Violence',
  near_miss: 'Near-Miss',
  compliance: 'Compliance',
  other: 'Other',
};

const Logs: React.FC = () => {
  const [templates, setTemplates] = useState<LogTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LogTemplate | null>(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/log-templates/');
      setTemplates(response.data.results || response.data || []);
      setError(null);
    } catch {
      setError('Failed to load log templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = !search || `${template.title} ${template.description}`.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !category || template.category === category;
    return matchesSearch && matchesCategory && template.is_current_version;
  });

  const handleCloseBuilder = () => {
    setBuilderOpen(false);
    setEditingTemplate(null);
    fetchTemplates();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Logs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build incident log templates for workplace violence, injuries, near-misses, and other required records.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingTemplate(null);
            setBuilderOpen(true);
          }}
        >
          New Template
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField label="Search" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 280 }} />
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Category</InputLabel>
          <Select value={category} label="Category" onChange={(event) => setCategory(event.target.value)}>
            <MenuItem value="">All Categories</MenuItem>
            {Object.entries(categoryLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" minHeight="300px" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography variant="h6">{template.title}</Typography>
                    <Chip size="small" label={template.version} />
                  </Box>
                  <Chip size="small" label={categoryLabels[template.category] || template.category} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 48 }}>
                    {template.description || 'No description provided.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.fields?.length || 0} fields
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setEditingTemplate(template);
                      setBuilderOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <LogTemplateBuilder open={builderOpen} onClose={handleCloseBuilder} template={editingTemplate} />
    </Box>
  );
};

export default Logs;
